import { Fragment } from 'react'
import Link from 'next/link'
import type { ArticleMoneyMapView } from '@/lib/blog/article-money-rules'
import { FILTERS_ROUTE_ROOT } from '@/lib/parts/fpn'

type NamedFilter = NonNullable<ArticleMoneyMapView['filters']>[number]

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

/**
 * FRIDGE-1087 — the owner: a filter article's "Shop the answer" offered
 * "Shop Refrigerators" while the article was about specific filters. When the
 * body names filter numbers whose pages ship, the module lists THOSE (code,
 * name, the genuine cartridge's FFN) plus the filter finder, and drops the
 * generic head. Same two placements, same light styling, same measurement
 * attributes (target "filter" / "finder").
 */
function ShopTheFilters({ filters, variant }: { filters: NamedFilter[]; variant: 'inline' | 'slim' }) {
  const finder = `${FILTERS_ROUTE_ROOT}#filter-finder`
  if (variant === 'slim') {
    return (
      <div
        data-shop-the-answer="slim"
        data-shop-the-answer-kind="filters"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px 12px',
          minHeight: 42,
          boxSizing: 'border-box',
          padding: '8px 14px',
          margin: '0 0 24px',
          background: '#f9fafb',
          border: '1px solid var(--gray-200, #e5e7eb)',
          borderRadius: 8,
          fontSize: 13.5,
          lineHeight: 1.4,
          color: '#374151',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--fridge-orange, #ff9951)' }}>
          Shop the answer
        </span>
        {filters.map((f, i) => (
          <Fragment key={f.code}>
            {i > 0 && (
              <span aria-hidden="true" style={{ color: '#9ca3af' }}>
                ·
              </span>
            )}
            <Link
              href={f.href}
              data-ph-capture-attribute-shop-the-answer="slim"
              data-ph-capture-attribute-shop-the-answer-target="filter"
              style={{ fontWeight: 700, fontFamily: MONO, color: 'var(--fridge-blue, #0469f0)', textDecoration: 'none' }}
            >
              {f.code}
            </Link>
          </Fragment>
        ))}
        <span aria-hidden="true" style={{ color: '#9ca3af' }}>
          ·
        </span>
        <Link
          href={finder}
          data-ph-capture-attribute-shop-the-answer="slim"
          data-ph-capture-attribute-shop-the-answer-target="finder"
          style={{ fontWeight: 600, color: 'var(--fridge-blue, #0469f0)', textDecoration: 'none' }}
        >
          Filter finder
        </Link>
      </div>
    )
  }
  return (
    <aside
      aria-label="Shop the answer"
      data-shop-the-answer="inline"
      data-shop-the-answer-kind="filters"
      style={{
        background: '#fff',
        border: '1px solid var(--gray-200, #e5e7eb)',
        borderLeft: '4px solid var(--fridge-orange, #ff9951)',
        borderRadius: 12,
        padding: '16px 20px',
        margin: '24px 0 28px',
        minHeight: 104,
        boxSizing: 'border-box',
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--fridge-orange, #ff9951)', marginBottom: 6 }}>
        Shop the answer
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.5, color: '#374151' }}>
        The filter{filters.length === 1 ? '' : 's'} this article names, every genuine and compatible option compared on Fridge.com:
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {filters.map((f) => (
          <Link
            key={f.code}
            href={f.href}
            data-ph-capture-attribute-shop-the-answer="inline"
            data-ph-capture-attribute-shop-the-answer-target="filter"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '2px 8px',
              padding: '8px 12px',
              borderRadius: 8,
              fontSize: 14,
              textDecoration: 'none',
              background: '#fff',
              color: 'var(--fridge-blue, #0469f0)',
              border: '1px solid var(--fridge-blue, #0469f0)',
            }}
          >
            <span style={{ fontFamily: MONO, fontWeight: 800 }}>{f.code}</span>
            {f.name ? <span style={{ fontWeight: 600, color: '#111827' }}>{f.name}</span> : null}
            {f.ffn ? (
              <span style={{ fontFamily: MONO, fontSize: 12, color: '#364153', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 6px' }}>
                {f.ffn}
              </span>
            ) : null}
          </Link>
        ))}
      </div>
      <p style={{ margin: '12px 0 0', fontSize: 13.5, lineHeight: 1.5, color: '#4b5563' }}>
        Not sure which one fits?{' '}
        <Link
          href={finder}
          data-ph-capture-attribute-shop-the-answer="inline"
          data-ph-capture-attribute-shop-the-answer-target="finder"
          style={{ color: 'var(--fridge-blue, #0469f0)', fontWeight: 600, textDecoration: 'none' }}
        >
          Find yours by refrigerator model
        </Link>
      </p>
    </aside>
  )
}

/**
 * "Shop the answer" — the article → head money-page conduit (FRIDGE-268
 * Phase 1). Two placements on every article that has a derivable head:
 *
 *   - `inline` — INSIDE the body, right after the answer paragraph (the
 *     `<!--STA-->` marker `insertShopTheAnswerMarker` places after the first
 *     paragraph of the first section). Compact light card, one line of copy
 *     naming the head collection (with its live product count) and the tool.
 *   - `slim` — the first screen, directly under the byline row, ahead of the
 *     takeaways, the bands and the sidebar rotation picks. One line.
 *
 * Both are ordinary internal <Link>s (indexable, no /r/, no nofollow) to a
 * collection and a tool — never an outbound affiliate link inside an article
 * body. Server-rendered inside the client template's HTML, no client fetch,
 * no image: the box has a fixed minimum height and paints once, so it adds
 * zero layout shift. Light background only (owner rule). Renders nothing
 * unless the server verified the head live (`data.head` is always verified
 * status='active' + seo_disposition='indexed' + live_product_count>0).
 *
 * Copy rules: "Fridge.com" plain (never possessive), figures only from
 * `live_product_count`, no time words.
 *
 * Measurement: every link carries `data-ph-capture-attribute-shop-the-answer`
 * (variant) and `…-target` (head | tool). posthog-js autocapture (on in
 * app/providers.tsx) lifts those onto the `$autocapture` click event as the
 * properties `shop_the_answer` / `shop_the_answer_target`, so the conduit's
 * click-through can be read in PostHog without a custom event.
 */
export function ShopTheAnswer({ data, variant }: { data: ArticleMoneyMapView; variant: 'inline' | 'slim' }) {
  if (data.filters && data.filters.length > 0) return <ShopTheFilters filters={data.filters} variant={variant} />
  const { head, tool } = data
  const count = head.liveProductCount > 0 ? new Intl.NumberFormat('en-US').format(head.liveProductCount) : null
  // FRIDGE-247 step 2 — the support row: the first-party data page the topic
  // supports, and the electricity-rate hub on running-cost and rebate topics.
  // Both come from the mapping (lib/blog/article-money-rules.ts), family-gated
  // there and already de-duplicated against the citation block, so this
  // template decides nothing: it renders what resolved, or nothing at all.
  const support = [
    data.data ? { key: 'data', label: data.data.title, url: data.data.url } : null,
    data.local ? { key: 'local', label: data.local.title, url: data.local.url } : null,
  ].filter((s): s is { key: string; label: string; url: string } => !!s)

  if (variant === 'slim') {
    return (
      <div
        data-shop-the-answer="slim"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px 12px',
          minHeight: 42,
          boxSizing: 'border-box',
          padding: '8px 14px',
          margin: '0 0 24px',
          background: '#f9fafb',
          border: '1px solid var(--gray-200, #e5e7eb)',
          borderRadius: 8,
          fontSize: 13.5,
          lineHeight: 1.4,
          color: '#374151',
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--fridge-orange, #ff9951)',
          }}
        >
          Shop the answer
        </span>
        <Link
          href={head.url}
          data-ph-capture-attribute-shop-the-answer="slim"
          data-ph-capture-attribute-shop-the-answer-target="head"
          style={{ fontWeight: 700, color: 'var(--fridge-blue, #0469f0)', textDecoration: 'none' }}
        >
          {head.title}
          {count ? ` (${count})` : ''}
        </Link>
        {tool && (
          <>
            <span aria-hidden="true" style={{ color: '#9ca3af' }}>
              ·
            </span>
            <Link
              href={tool.url}
              data-ph-capture-attribute-shop-the-answer="slim"
              data-ph-capture-attribute-shop-the-answer-target="tool"
              style={{ fontWeight: 600, color: 'var(--fridge-blue, #0469f0)', textDecoration: 'none' }}
            >
              {tool.name}
            </Link>
          </>
        )}
        {/*
          Separator and link are SIBLING flex items, exactly as the tool above
          renders them — one spacing system for the whole row, so the `gap`
          alone spaces them and no item carries padding of its own.
        */}
        {support.map((s) => (
          <Fragment key={s.key}>
            <span aria-hidden="true" style={{ color: '#9ca3af' }}>
              ·
            </span>
            <Link
              href={s.url}
              data-ph-capture-attribute-shop-the-answer="slim"
              data-ph-capture-attribute-shop-the-answer-target={s.key}
              style={{ fontWeight: 600, color: 'var(--fridge-blue, #0469f0)', textDecoration: 'none' }}
            >
              {s.label}
            </Link>
          </Fragment>
        ))}
      </div>
    )
  }

  return (
    <aside
      aria-label="Shop the answer"
      data-shop-the-answer="inline"
      style={{
        background: '#fff',
        border: '1px solid var(--gray-200, #e5e7eb)',
        borderLeft: '4px solid var(--fridge-orange, #ff9951)',
        borderRadius: 12,
        padding: '16px 20px',
        margin: '24px 0 28px',
        minHeight: 104,
        boxSizing: 'border-box',
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--fridge-orange, #ff9951)',
          marginBottom: 6,
        }}
      >
        Shop the answer
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.5, color: '#374151' }}>
        <strong style={{ color: '#111827' }}>{head.title}</strong>
        {count ? `: ${count} live models on Fridge.com` : ' on Fridge.com'}
        {tool ? (
          <>
            , plus the <strong style={{ color: '#111827' }}>{tool.name}</strong>.
          </>
        ) : (
          '.'
        )}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <Link
          href={head.url}
          data-ph-capture-attribute-shop-the-answer="inline"
          data-ph-capture-attribute-shop-the-answer-target="head"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 14px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
            background: 'var(--fridge-blue, #0469f0)',
            color: '#fff',
          }}
        >
          Shop {head.title}
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
        {tool && (
          <Link
            href={tool.url}
            data-ph-capture-attribute-shop-the-answer="inline"
            data-ph-capture-attribute-shop-the-answer-target="tool"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '9px 14px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              textDecoration: 'none',
              background: '#fff',
              color: 'var(--fridge-blue, #0469f0)',
              border: '1px solid var(--fridge-blue, #0469f0)',
            }}
          >
            {tool.name}
          </Link>
        )}
      </div>
      {support.length > 0 && (
        <p style={{ margin: '12px 0 0', fontSize: 13.5, lineHeight: 1.5, color: '#4b5563' }}>
          The data behind it:{' '}
          {support.map((s, i) => (
            <span key={s.key}>
              {i > 0 && <span style={{ color: '#9ca3af', padding: '0 6px' }}>·</span>}
              <Link
                href={s.url}
                data-ph-capture-attribute-shop-the-answer="inline"
                data-ph-capture-attribute-shop-the-answer-target={s.key}
                style={{ color: 'var(--fridge-blue, #0469f0)', fontWeight: 600, textDecoration: 'none' }}
              >
                {s.label}
              </Link>
            </span>
          ))}
        </p>
      )}
    </aside>
  )
}
